import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import './ChatThread.css';

/**
 * Shared chat component used by both practitioner (inside P2 Messages tab)
 * and client (C5 Messages screen).
 *
 * Props:
 *  - clientId: the client record id
 *  - practitionerId: the practitioner record id
 *  - recipientName: display name of the other party
 */
export default function ChatThread({ clientId, practitionerId, recipientName }) {
  const { user, role } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    loadMessages();
    const cleanup = subscribeToMessages();
    return cleanup;
  }, [clientId, practitionerId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  async function loadMessages() {
    setLoading(true);

    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('client_id', clientId)
      .eq('practitioner_id', practitionerId)
      .order('created_at', { ascending: true });

    setMessages(data || []);
    setLoading(false);

    // Mark unread messages as read
    markAsRead(data || []);
  }

  async function markAsRead(msgs) {
    const unread = msgs.filter(
      (m) => m.sender_id !== user.id && !m.read_at
    );
    if (unread.length === 0) return;

    const ids = unread.map((m) => m.id);
    await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .in('id', ids);
  }

  function subscribeToMessages() {
    const channel = supabase
      .channel(`messages:${clientId}:${practitionerId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `client_id=eq.${clientId}`,
        },
        (payload) => {
          const newMsg = payload.new;
          // Only add if it matches our practitioner
          if (newMsg.practitioner_id === practitionerId) {
            setMessages((prev) => {
              // Avoid duplicates
              if (prev.some((m) => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });

            // Mark as read if it's from the other party
            if (newMsg.sender_id !== user.id) {
              supabase
                .from('messages')
                .update({ read_at: new Date().toISOString() })
                .eq('id', newMsg.id)
                .then(() => {});
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  function scrollToBottom() {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  async function handleSend(e) {
    e.preventDefault();
    const body = newMessage.trim();
    if (!body || sending) return;

    setSending(true);
    setNewMessage('');

    const senderRole = role === 'client' ? 'client' : 'practitioner';

    const { data, error } = await supabase
      .from('messages')
      .insert({
        sender_id: user.id,
        sender_role: senderRole,
        client_id: clientId,
        practitioner_id: practitionerId,
        body,
      })
      .select()
      .single();

    if (data) {
      // Add locally in case realtime hasn't delivered it yet
      setMessages((prev) => {
        if (prev.some((m) => m.id === data.id)) return prev;
        return [...prev, data];
      });
    }

    setSending(false);
  }

  function formatTime(dateStr) {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();

    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    if (isToday) return time;

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;

    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time;
  }

  function isMine(msg) {
    return msg.sender_id === user.id;
  }

  if (loading) {
    return <div className="chat-loading">Loading messages...</div>;
  }

  return (
    <div className="chat-thread">
      <div className="chat-messages" ref={containerRef}>
        {messages.length === 0 ? (
          <div className="chat-empty">
            <p>No messages yet.</p>
            <p className="chat-empty-sub">
              Start a conversation with {recipientName}.
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => {
              const mine = isMine(msg);
              // Show date separator if day changes
              const prevMsg = messages[i - 1];
              const showDate = !prevMsg ||
                new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();

              return (
                <div key={msg.id}>
                  {showDate && (
                    <div className="chat-date-sep">
                      {new Date(msg.created_at).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                  )}
                  <div className={`chat-bubble-row ${mine ? 'chat-mine' : 'chat-theirs'}`}>
                    <div className={`chat-bubble ${mine ? 'bubble-mine' : 'bubble-theirs'}`}>
                      <p className="chat-body">{msg.body}</p>
                      <span className="chat-time">{formatTime(msg.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <form className="chat-input-bar" onSubmit={handleSend}>
        <input
          type="text"
          className="chat-input"
          placeholder={`Message ${recipientName}...`}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          autoComplete="off"
        />
        <button
          type="submit"
          className="chat-send-btn"
          disabled={!newMessage.trim() || sending}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
    </div>
  );
}
